import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";

import _ from "lodash";
import ListItem from "../../components/ListItem/ListItem.jsx";

import Switch from "../../components/Switch/Switch.jsx";
import WrapperContainer from "../../components/WrapperContainer/WrapperContainer.jsx";

import Tooltip from "../../components/Tooltip/Tooltip.jsx";
import SvgIcon from "../../components/Icon/SvgIcon.jsx";

import { SERVICES_HOME } from "../../utils/consts/service";
import getClassNames from "../../utils/helpers/getClassNames.js";

const ServiceHome = ({
  title,
  tariffsCurrentService,
  tariffsAllServices,
  indexCurrent,
  indexActive,
  isDisabled,
  isExist,
  isAutoSelect,
  changeIndexActive,
  getTariff,
}) => {
  const refSvgTooltip = useRef();

  const [selectedTariff, setSelectedTariff] = useState({});
  const [isSpecialInetTariff, setIsSpecialInetTariff] = useState(false);

  const isServiceInet = title === SERVICES_HOME.INTERNET.name;
  const isServiceTv = title === SERVICES_HOME.TV.name;

  const isServiceInetTv = title === SERVICES_HOME.INTERNET_TV.name;
  const isServiceInetIptv = title === SERVICES_HOME.INTERNET_IPTV.name;

  const isServiceIptv = title === SERVICES_HOME.IPTV.name;
  const isServiceIptvMixed = isServiceIptv || isServiceInetIptv;

  /**
   * Благодаря пробросу из родителя свойства tariffsAllServices становиться возможным
   * установить зависимости услуг друг от друга при изменении конкретной из них
   */
  const hasTariffInet = tariffsAllServices.some(
    tariff => tariff.service === SERVICES_HOME.INTERNET.name
  );

  const hasTariffTv = tariffsAllServices.some(tariff => tariff.service === SERVICES_HOME.TV.name);
  const hasTariffIptv = tariffsAllServices.some(
    tariff => tariff.service === SERVICES_HOME.IPTV.name
  );

  /**
   * Признак отображения перечня тарифов.
   * Тарифы не отображаются для услуг УфанетТВ/ТВскоп/Телевидение
   */
  const isVisibleTariffs = indexActive === indexCurrent && !isServiceIptv && !isServiceTv;

  /**
   * Признак того, что тумблер услуги переключен в состояние Активен.
   * Для одних услуг это предполагает выбор тарифа, для других отображение перечня тарифов
   */
  const isActive =
    isExist ||
    indexActive === indexCurrent ||
    (!(indexActive === indexCurrent) && !_.isEmpty(selectedTariff));

  useEffect(() => {
    /**
     * При изменении состояния доступности тумблера к переключению
     * необходимо сбрасывать информацию о ранее выбранном тарифе услуги
     */
    if (isDisabled) {
      setSelectedTariff({});
    }
  }, [isDisabled]);

  useEffect(() => {
    /**
     * При выборе определенных услуг необходимо следить за актуальностью
     * стоимости для услуги УфанетТВ/ТВскоп
     */
    if (isServiceIptv && hasTariffIptv) {
      setTariff();
    }
  }, [tariffsAllServices.length]);

  /**
   * Получить иконку услуги
   *
   * @param {Object} tariff
   * @return {JSX.Element}
   */
  const getIcon = tariff => {
    const { parameterOne } = tariff;

    /**
     * Для услуги Интернет (в том числе в рамках пакета) иконка устанавливается
     * в зависимости от скорости тарифа
     */
    if (isServiceInet || isServiceInetTv || isServiceInetIptv) {
      if (parameterOne < 200) {
        return <SvgIcon name="speedLow" />;
      } else if (parameterOne >= 200 && parameterOne < 600) {
        return <SvgIcon name="speedMedium" />;
      } else {
        return <SvgIcon name="speedHigh" />;
      }
    }
  };

  /**
   * Получить вспомогательную информацию по услуге
   *
   * @param {Object} tariff
   * @param {Boolean} isOtherDescription
   * @return {String || JSX.Element}
   */
  const getDescription = (tariff, isOtherDescription = false) => {
    const { parameterOne } = tariff;

    if (isServiceIptv && isDisabled && !isActive) {
      return "Доступно с услугой интернет";
    }

    if (hasTariffInet && hasTariffTv && isServiceIptv && !isActive) {
      return "При подключении 3-х услуг";
    }

    if (isServiceInet) {
      return `до ${parameterOne} МБит/с`;
    }

    if (isServiceInetTv || isServiceInetIptv) {
      const text = `до ${parameterOne} МБит/с`;

      if (!isOtherDescription) {
        return (
          <div>
            {text}
            <br />
            <WrapperContainer text="sm" className="text-color-dark-gray">
              более 200 каналов
            </WrapperContainer>
          </div>
        );
      }

      return text;
    }
  };

  /**
   * Передать информацию о выбранном тарифе в родителя для подсчета общей стоимости услуг.
   * Также в передаваемый объект тарифа записываем дополнительные ключи
   *
   * @param {Object} tariff
   */
  const selectTariff = tariff => {
    const { service: serviceName, parameterOne } = tariff;

    if (serviceName === SERVICES_HOME.INTERNET.name) {
      setIsSpecialInetTariff(parseInt(parameterOne) > 100);
    }

    setSelectedTariff(tariff);
    getTariff(tariff);
  };

  /**
   * Выбор услуги и тарифа для подключения.
   * Для некоторых услуг выбор тарифа является явным.
   * Для других тариф выбирается автоматически по нажатию на услугу
   */
  const handleClickHeader = event => {
    event.preventDefault();

    // В случае срабатывания клика на тултипе игнорируем дальнейшую логику
    if (event.target.classList.contains("clickable")) {
      return;
    }

    // Если тариф выбирается автоматически
    if (isAutoSelect) {
      if (_.isEmpty(selectedTariff)) {
        setTariff();
        changeIndexActive(indexCurrent);
      } else {
        deleteTariff();
      }
    }

    // Если тариф выбирается вручную
    if (!isAutoSelect) {
      /**
       * Если клик по блоку с услугой, когда тариф НЕ выбран или клик НЕ по компоненту Switch,
       * то в таком случае нужно просто скрыть/отобразить перечень тарифов
       */
      if (_.isEmpty(selectedTariff) || event.target.className !== "switch-input__slider") {
        changeIndexActive(indexCurrent);
      } else {
        // Сброс специфичных состояний для определенных услуг
        if (isServiceInet) {
          setIsSpecialInetTariff(false);
        }

        deleteTariff();
      }
    }
  };

  /**
   * Удалить выбранный тариф из спика на подключение
   */
  const deleteTariff = () => {
    setSelectedTariff({});
    changeIndexActive(-1);
    getTariff(selectedTariff, true);
  };

  /**
   * Установить тарифы для услуг Телевидение/УфанетТВ/ТВскоп
   */
  const setTariff = () => {
    if (isServiceIptv && !isDisabled) {
      // В случае если на адресе не подразумевается скидка для УфанетТВ/ТВскоп
      if (tariffsCurrentService.length === 1) {
        selectTariff(tariffsCurrentService[0]);
        return;
      }

      /**
       * В дрс если выбраны услуги Интернет и Телевидение,
       * то стоимость для услуги УфанетТВ/ТВскоп будет отличаться от стоимости
       * без подключаемой услуги Телевидение
       */
      if (hasTariffInet && hasTariffTv) {
        selectTariff(tariffsCurrentService[0]);
      } else {
        selectTariff(tariffsCurrentService[1]);
      }
    }

    if (isServiceTv && !isDisabled) {
      selectTariff(tariffsCurrentService[0]);
    }
  };

  const classServiceTariffs = getClassNames([
    "service-tariffs",
    isServiceInet && isSpecialInetTariff && "text-vgap16",
  ]);

  const getClassServiceTariff = tariffTitle =>
    getClassNames([
      "service-tariff",
      selectedTariff.title === tariffTitle && "service-tariff--selected",
    ]);

  /**
   * Получить название услуги
   *
   * @return {JSX.Element|String}
   */
  const getTitle = () => {
    const _title = SERVICES_HOME[title].title;
    if (isServiceIptvMixed) {
      return (
        <>
          {_title}
          <Tooltip content={"Уфанет ТВ – телевидение для SmartTV, смартфонов и планшетов"}>
            <SvgIcon name="help" color="#AAABAD" className="clickable" ref={refSvgTooltip} />
          </Tooltip>
        </>
      );
    }

    return _title;
  };

  // Признак отображения информации о выбранном тарифе
  const isVisibleTariffInfo = selectedTariff?.parameterOne && !isDisabled;

  // Признак отображения информации о недоступности услуги
  const isVisibleAvailabilityServiceInfo = isServiceIptv && isDisabled;

  // Признак отображения информации о скидке на услугу УфанетТВ/ТВскоп
  const isVisibleSaleInfo = isServiceIptv && hasTariffInet && hasTariffIptv && hasTariffTv;
  const isVisibleServiceInfo =
    isVisibleTariffInfo || isVisibleAvailabilityServiceInfo || isVisibleSaleInfo;

  const tariffCost = selectedTariff.cost;

  if (!tariffsCurrentService.length) return null;

  return (
    <div>
      <ListItem
        isRounded
        title={getTitle()}
        desc={isVisibleServiceInfo ? getDescription(selectedTariff, true) : ""}
        icon={SERVICES_HOME[title].icon}
        color="#2A2E35"
        bg="#F4F4F4"
        handleClick={!isDisabled ? handleClickHeader : null}
      >
        {tariffCost && !isDisabled && <div className="text-medium">{tariffCost} ₽/мес.</div>}
        <Switch onChange={() => null} checked={isActive} disabled={isDisabled} />
      </ListItem>

      {isVisibleTariffs && (
        <>
          <WrapperContainer gap={isServiceIptvMixed ? "16" : ""} className={classServiceTariffs}>
            {tariffsCurrentService.map((tariff, index) => (
              <div
                className={getClassServiceTariff(tariff.title)}
                key={index}
                onClick={() => selectTariff(tariff)}
              >
                {getIcon(tariff)}
                <div className="service-tariff__param">{getDescription(tariff)}</div>
                <div className="service-tariff__cost text-medium">{tariff.cost} руб/мес</div>
              </div>
            ))}
          </WrapperContainer>

          {isServiceIptvMixed && (
            <WrapperContainer text="md" align="right" className="text-color-dark-gray">
              Если нет СмартТВ – одну приставку даем за 0₽.
            </WrapperContainer>
          )}

          {isServiceInet && isSpecialInetTariff && (
            <WrapperContainer text="md" className="text-color-dark-gray">
              Для данного тарифа, возможно, понадобится выезд инженера.
            </WrapperContainer>
          )}
        </>
      )}
    </div>
  );
};

ServiceHome.propTypes = {
  title: PropTypes.string.isRequired,
  tariffsCurrentService: PropTypes.array,
  tariffsAllServices: PropTypes.array.isRequired,
  getTariff: PropTypes.func.isRequired,
  indexCurrent: PropTypes.number.isRequired,
  indexActive: PropTypes.number.isRequired,
  changeIndexActive: PropTypes.func.isRequired,
  isDisabled: PropTypes.bool.isRequired,
  isExist: PropTypes.bool.isRequired,
  isAutoSelect: PropTypes.bool,
};

ServiceHome.defaultProps = {
  isAutoSelect: false,
};

export default ServiceHome;
